const avatarImports: Record<string, string> = import.meta.glob(
  "@/assets/images/avatars/*.png",
  { eager: true, import: "default", query: "?url" },
) as Record<string, string>;

function getLetterAvatarUrl(name: string): string {
  const letter = (name[0] ?? "").toLowerCase();
  const match =
    Object.entries(avatarImports).find(([path]) =>
      path.endsWith(`/${letter}.png`),
    )?.[1] ??
    Object.entries(avatarImports).find(([path]) =>
      path.endsWith("/default.png"),
    )?.[1];
  return match ?? "";
}

/**
 * Resolves initial display name from auth data.
 * Prefers displayName, falls back to the part before @ in email.
 */
export function resolveInitialName(
  displayName: string | null | undefined,
  email: string | null | undefined,
): string {
  if (displayName?.trim()) {
    return displayName.trim();
  }
  if (email?.includes("@")) {
    return email.split("@")[0] ?? "";
  }
  return "";
}

/**
 * Resolves initial profile picture as a File object.
 * Tries photoURL first (e.g. Google), falls back to a static letter avatar.
 */
export async function resolveInitialProfilePicture(
  photoURL: string | null | undefined,
  name: string,
): Promise<File> {
  if (photoURL) {
    try {
      const response = await fetch(photoURL);
      const blob = await response.blob();
      return new File([blob], "profile.jpg", { type: blob.type });
    } catch {
      // Fall through to letter avatar
    }
  }

  const avatarUrl = getLetterAvatarUrl(name);
  const response = await fetch(avatarUrl);
  const blob = await response.blob();
  return new File([blob], "avatar.png", { type: "image/png" });
}
